"use client";

// Re-export sonner's toast function with a compatible API
// This replaces the Radix-based toast that had compose-refs infinite loop bugs
import { toast as sonnerToast } from "sonner";

import type {
  ToastActionElement,
  ToastProps,
} from "@/app/_module/components/ui/toast";

type ToasterToast = ToastProps & {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: ToastActionElement;
};

type Toast = Omit<ToasterToast, "id">;

// Adapter function to map our toast API to sonner's API
function toast({ title, description, variant, action, duration, className, ...props }: Toast & { duration?: number; className?: string }) {
  const opts: Record<string, unknown> = {
    description: description as string,
    ...(duration && { duration }),
    ...(className && { className }),
  };

  // Support sonner action format: { label, onClick } passed directly
  // Also support React element actions by extracting altText + onClick from props
  if (action) {
    const actionProps = (action as any)?.props;
    if (actionProps?.onClick) {
      opts.action = {
        label: actionProps?.altText || actionProps?.children || 'Undo',
        onClick: actionProps.onClick,
      };
    } else if (typeof action === 'object' && 'label' in (action as any)) {
      opts.action = action;
    }
  }

  // Map variant to sonner's types
  if (variant === "destructive") {
    return sonnerToast.error(title as string, opts);
  }

  return sonnerToast(title as string, opts);
}

// Hook for compatibility - returns empty toasts array since sonner manages its own state
function useToast() {
  return {
    toasts: [] as ToasterToast[],
    toast,
    dismiss: (toastId?: string) => {
      if (toastId) {
        sonnerToast.dismiss(toastId);
      } else {
        sonnerToast.dismiss();
      }
    },
  };
}

// Keep the reducer export for backwards compatibility (though it won't be used)
const reducer = (state: { toasts: ToasterToast[] }) => state;

export { useToast, toast, reducer };
